import styled from 'styled-components';

import {SubscriptionStatusEnum} from '@/entities/subscriptions/types';
import {border, colors, font} from '@app/styles/theme';

export const Wrapper = styled.div`
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 20px;
    background-color: ${colors.white};
    padding: 24px 32px 32px 32px;
`;

export const Title = styled.h1`
    font-family: ${font.family.primary};
    font-size: 24px;
    font-weight: 600;
    line-height: 36px;
    color: ${colors.textPrimary};
    margin: 0;
`;

export const Card = styled.div`
    border: 1px solid ${colors.border};
    border-radius: ${border.radius};
    background: ${colors.white};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    overflow: hidden;
`;

export const CardHeader = styled.div`
    padding: 20px 24px 0;
`;

export const CardHeaderContentWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
`;

export const CardHeaderContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const CardHeaderControl = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
`;

export const SubscriptionRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
`;

export const SubscriptionLabel = styled.span`
    font-family: ${font.family.primary};
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    color: ${colors.textGrey1};
`;

export const ActiveBadge = styled.span<{$status: SubscriptionStatusEnum}>`
    display: inline-flex;
    align-items: center;
    padding: 6px 14px;
    border-radius: 999px;
    background: ${(p) => {
        switch (p.$status) {
            case SubscriptionStatusEnum.ACTIVE:
                return colors.success;
            case SubscriptionStatusEnum.CANCELLED:
                return colors.danger;
            case SubscriptionStatusEnum.PAYMENT_PENDING:
                return colors.warningSecondary;
            default:
                return colors.danger;
        }
    }};
    color: ${colors.white};
    font-family: ${font.family.primary};
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
`;

export const HeaderDivider = styled.div`
    margin-top: 16px;
    height: 1px;
    background: ${colors.border};
    width: 100%;
`;

export const Row = styled.div<{noBorder?: boolean}>`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px 24px;
    padding: 18px 24px;
    border-bottom: ${(p) => (p.noBorder ? 'none' : `1px solid ${colors.border}`)};

    &:last-of-type {
        border-bottom: none;
    }

    @media (max-width: 600px) {
        flex-direction: column;
        gap: 8px;
    }
`;

export const RowLabel = styled.div<{fullWidth?: boolean}>`
    width: ${(p) => (p.fullWidth ? '100%' : '160px')};
    font-family: ${font.family.primary};
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: ${colors.textGrey1};

    @media (max-width: 600px) {
        flex: none;
    }
`;

export const RowValue = styled.div`
    flex: 1 1 auto;
    min-width: 0;
    font-family: ${font.family.primary};
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: ${colors.textGrey1};
`;

export const PlanChips = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
`;

export const PlanChipPrimary = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 6px 16px;
    border-radius: 999px;
    background: ${colors.brandPrimary};
    color: ${colors.white};
    font-family: ${font.family.primary};
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
`;

export const PlanChipPending = styled(PlanChipPrimary)`
    background: #bebebe;
    color: ${colors.white};
`;

export const PlanChipSoft = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 8px 14px;
    border-radius: 999px;
    background: ${colors.brandLight};
    color: ${colors.textGrey1};
    font-family: ${font.family.primary};
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
`;

export const PeriodText = styled.span`
    font-family: ${font.family.primary};
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    color: ${colors.textSecondary};
`;

export const PeriodDate = styled.span`
    font-family: ${font.family.primary};
    font-size: 14px;
    font-weight: 600;
    line-height: 22px;
    color: ${colors.textGrey1};
`;

export const BalanceBlock = styled.div`
    flex: 1 1 auto;
    min-width: 0;
    max-width: 400px;
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

export const BalanceHeadline = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px 16px;
`;

export const BalanceHeadLeft = styled.span`
    font-family: ${font.family.primary};
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: ${colors.textGrey1};
`;

export const BalanceHeadRight = styled.span`
    font-family: ${font.family.primary};
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: ${colors.textSecondary};
    text-align: right;
`;

export const BalanceHeadEm = styled.strong`
    font-weight: 600;
    color: ${colors.textGrey1};
`;

export const ProgressTrack = styled.div`
    width: 100%;
    height: 10px;
    border-radius: 999px;
    background: ${colors.border};
    overflow: hidden;
`;

export const ProgressFill = styled.div<{$pct: number}>`
    height: 100%;
    width: ${(p) => p.$pct}%;
    max-width: 100%;
    border-radius: 999px;
    background: ${colors.brandPrimary};
    transition: width 0.25s ease;
`;

export const LegendRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 24px 32px;
`;

export const LegendItem = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: ${font.family.primary};
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: ${colors.textSecondary};
`;

export const LegendDot = styled.span<{$variant: 'primary' | 'muted'}>`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${(p) => (p.$variant === 'primary' ? colors.brandPrimary : colors.border)};
`;

export const LegendStrong = styled.strong`
    font-weight: 600;
    color: ${colors.textGrey1};
`;

export const FallbackText = styled.p`
    font-family: ${font.family.primary};
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: ${colors.textGrey1};
    margin: 0;
`;

export const Footer = styled.div`
    display: flex;
    justify-content: flex-start;
    gap: 16px;
`;

export const CanceledText = styled.p`
    font-family: ${font.family.primary};
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: ${colors.textGrey1};
    margin: 0;
`;

export const EmailLink = styled.a`
    color: ${colors.brandPrimary};
    font-family: ${font.family.primary};
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
        color: ${colors.brandDark};
    }
`;
